import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';

const CATEGORY_PRODUCT_TYPES = {
    "Home & Kitchen": ["Kitchen Appliances", "Home Decor", "Cookware", "Storage Solutions", "Cleaning Supplies", "Furniture", "Lighting", "Bedding", "Bath Accessories"],
    "Beauty & Personal Care": ["Skincare", "Makeup", "Hair Care", "Fragrances", "Personal Hygiene", "Oral Care", "Body Care", "Beauty Tools", "Nail Care"],
    "Clothing, Shoes & Jewelry": ["Women's Clothing", "Men's Clothing", "Shoes", "Jewelry", "Watches", "Handbags", "Accessories", "Athletic Wear", "Formal Wear"],
    "Electronics": ["Smartphones", "Laptops", "Headphones", "Tablets", "Smartwatches", "Gaming Accessories", "Cameras", "Audio Equipment", "Computer Accessories"],
    "Health & Household": ["Vitamins & Supplements", "Medical Supplies", "Household Essentials", "Baby Care", "Wellness Products", "First Aid", "Health Monitors", "Mobility Aids"],
    "Toys & Games": ["Educational Toys", "Action Figures", "Board Games", "Building Blocks", "Dolls", "Electronic Toys", "Outdoor Play", "Puzzles", "Arts & Crafts"],
    "Sports & Outdoors": ["Fitness Equipment", "Outdoor Gear", "Sports Apparel", "Camping Equipment", "Exercise Accessories", "Team Sports", "Water Sports", "Winter Sports"],
    "Baby": ["Baby Feeding", "Diapers", "Baby Clothing", "Strollers", "Car Seats", "Baby Monitors", "Baby Toys", "Nursery Furniture", "Baby Safety"],
    "Pet Supplies": ["Dog Supplies", "Cat Supplies", "Pet Food", "Pet Toys", "Pet Health", "Pet Grooming", "Aquarium Supplies", "Bird Supplies", "Small Animal Care"],
    "Automotive Parts & Accessories": ["Car Electronics", "Interior Accessories", "Exterior Accessories", "Tools & Equipment", "Oils & Fluids", "Replacement Parts", "Tires & Wheels"],
    "Appliances": ["Kitchen Appliances", "Laundry Appliances", "Small Appliances", "Vacuum Cleaners", "Air Conditioners", "Heating Systems", "Water Heaters"],
    "Arts, Crafts & Sewing": ["Drawing & Painting", "Sewing Supplies", "Craft Kits", "Scrapbooking", "Jewelry Making", "Fabric", "Art Supplies", "Craft Tools"],
    "Books": ["Fiction", "Non-Fiction", "Educational", "Children's Books", "Self-Help", "Biographies", "Textbooks", "Reference Books"],
    "Cell Phones & Accessories": ["Smartphones", "Phone Cases", "Chargers", "Screen Protectors", "Phone Holders", "Bluetooth Accessories", "Phone Parts"],
    "Computers": ["Laptops", "Desktop Computers", "Computer Parts", "Monitors", "Keyboards & Mice", "Software", "Networking Equipment", "Storage Devices"],
    "Garden & Outdoor": ["Garden Tools", "Plants & Seeds", "Outdoor Furniture", "Grills & Outdoor Cooking", "Lawn Care", "Garden Decor", "Outdoor Lighting"],
    "Grocery & Gourmet Food": ["Snacks", "Beverages", "Gourmet Foods", "Organic Products", "International Foods", "Health Foods", "Cooking Ingredients"],
    "Industrial & Scientific": ["Lab Equipment", "Industrial Tools", "Safety Equipment", "Measuring Instruments", "Industrial Supplies", "Scientific Instruments"],
    "Kitchen & Dining": ["Cookware", "Kitchen Appliances", "Dinnerware", "Kitchen Tools", "Food Storage", "Kitchen Linens", "Barware", "Coffee & Tea"],
    "Musical Instruments": ["Guitars", "Keyboards", "Drums", "Wind Instruments", "String Instruments", "Audio Equipment", "Music Accessories", "Sheet Music"],
    "Office Products": ["Office Supplies", "Office Furniture", "Office Electronics", "Filing & Organization", "Writing & Correction", "Presentation Supplies"],
    "Patio, Lawn & Garden": ["Patio Furniture", "Garden Tools", "Lawn Mowers", "Outdoor Decor", "Plant Care", "Watering Equipment", "Outdoor Storage"],
    "Tools & Home Improvement": ["Power Tools", "Hand Tools", "Hardware", "Electrical", "Plumbing", "Paint & Supplies", "Storage & Organization", "Safety Equipment"],
    "Video Games": ["Gaming Consoles", "Video Game Software", "Gaming Accessories", "Gaming Headsets", "Controllers", "Gaming Chairs", "Gaming Monitors"],
    "Collectibles & Fine Art": ["Collectible Toys", "Antiques", "Coins", "Stamps", "Trading Cards", "Fine Art", "Memorabilia", "Vintage Items"],
    "CDs & Vinyl": ["Pop Music", "Rock Music", "Classical", "Jazz", "Country", "Hip-Hop", "Electronic", "Vinyl Records", "Music Accessories"],
    "Handmade": ["Handmade Jewelry", "Handmade Home Decor", "Handmade Clothing", "Artisan Crafts", "Custom Items", "Handmade Toys", "Personalized Gifts"],
    "Movies & TV": ["DVDs", "Blu-ray", "Digital Movies", "TV Shows", "Documentary Films", "Foreign Films", "Classic Movies", "Movie Merchandise"],
    "Software": ["Operating Systems", "Business Software", "Security Software", "Design Software", "Gaming Software", "Educational Software", "Utilities"],
};
const CATEGORIES = Object.keys(CATEGORY_PRODUCT_TYPES);
const PRODUCT_TYPES = CATEGORY_PRODUCT_TYPES;

export default function Dashboard() {
    const { user, authFetch } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [genError, setGenError] = useState('');
    const [genSuccess, setGenSuccess] = useState('');
    const [category, setCategory] = useState('Electronics');
    const [productType, setProductType] = useState('Headphones');
    const [showGenForm, setShowGenForm] = useState(false);
    const [minRoi, setMinRoi] = useState('10');
    const [apify, setApify]           = useState(null);
    const [outreach, setOutreach]     = useState(null);
    const [launching, setLaunching]   = useState(false);
    const [launchMsg, setLaunchMsg]   = useState('');
    const [triggeringCalls, setTriggeringCalls] = useState(false);
    const [callMsg, setCallMsg]       = useState('');

    const paymentSuccess = new URLSearchParams(location.search).get('payment') === 'success';

    useEffect(() => {
        fetchDashboard();
        fetchApifyStatus();
        fetchOutreachStatus();
    }, []);

    const fetchDashboard = async () => {
        try {
            const res = await authFetch('/api/dashboard');
            if (res.ok) {
                const d = await res.json();
                setData(d);
            }
        } catch (err) {
            console.error('Dashboard error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchApifyStatus = async () => {
        try {
            const res = await authFetch('/api/reports/apify/status');
            if (res.ok) setApify(await res.json());
        } catch (_) {}
    };

    const fetchOutreachStatus = async () => {
        try {
            const res = await authFetch('/api/outreach/status');
            if (res.ok) setOutreach(await res.json());
        } catch (_) {}
    };

    const launchOutreach = async () => {
        setLaunching(true);
        setLaunchMsg('');
        try {
            const res = await authFetch('/api/outreach/launch', { method: 'POST' });
            const d = await res.json();
            setLaunchMsg(d.message || (d.ok ? 'Outreach launched!' : 'Could not launch.'));
            const poll = setInterval(async () => {
                const sr = await authFetch('/api/outreach/status');
                if (sr.ok) {
                    const s = await sr.json();
                    setOutreach(s);
                    if (!s.running) { clearInterval(poll); setLaunching(false); }
                }
            }, 5000);
        } catch (err) {
            setLaunchMsg('Error: ' + err.message);
            setLaunching(false);
        }
    };

    const triggerCalls = async () => {
        setTriggeringCalls(true);
        setCallMsg('');
        try {
            const res = await authFetch('/api/outreach/trigger-calls', { method: 'POST' });
            const d = await res.json();
            setCallMsg(d.message || (d.ok ? 'Calls triggered!' : 'Could not trigger calls.'));
            const poll = setInterval(async () => {
                const sr = await authFetch('/api/outreach/status');
                if (sr.ok) {
                    const s = await sr.json();
                    setOutreach(s);
                    if (!s.calling) { clearInterval(poll); setTriggeringCalls(false); }
                }
            }, 3000);
        } catch (err) {
            setCallMsg('Error: ' + err.message);
            setTriggeringCalls(false);
        }
    };

    const generateReport = async () => {
        setGenerating(true);
        setGenError('');
        setGenSuccess('');
        try {
            const res = await authFetch('/api/reports/generate', {
                method: 'POST',
                body: JSON.stringify({ category, product_type: productType, min_roi: minRoi })
            });
            const d = await res.json();
            if (!res.ok) throw new Error(d.error || 'Generation failed');
            setGenSuccess(`Report generated! "${d.report.category} — ${d.report.product_type}" is ready to download.`);
            setShowGenForm(false);
            fetchDashboard();
        } catch (err) {
            setGenError(err.message);
        } finally {
            setGenerating(false);
        }
    };

    const checkout = async (plan) => {
        try {
            const res = await authFetch('/api/stripe/create-checkout', {
                method: 'POST',
                body: JSON.stringify({ plan })
            });
            const d = await res.json();
            if (!res.ok) throw new Error(d.error);
            window.location.href = d.url;
        } catch (err) {
            setGenError(err.message);
        }
    };

    if (loading) return <LoadingSpinner fullPage text="Loading dashboard..." />;

    const stats = data?.stats || {};
    const hasActiveSub = stats.hasSubscription;
    const planColors = { blue: '#0f3460', purple: '#6c3483', green: '#27ae60', gray: '#7f8c8d' };
    const planColor = planColors[stats.planColor] || '#7f8c8d';

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">
            {paymentSuccess && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3 text-green-700">
                    <i className="fas fa-check-circle text-xl"></i>
                    <div>
                        <div className="font-semibold">Payment successful!</div>
                        <div className="text-sm">Your plan is now active. You can start generating reports.</div>
                    </div>
                </div>
            )}

            {genSuccess && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3 text-green-700">
                    <i className="fas fa-file-check text-xl"></i>
                    <div>
                        <div className="font-semibold">{genSuccess}</div>
                        <Link to="/reports" className="text-sm font-medium" style={{ color: '#e94560' }}>View in My Reports →</Link>
                    </div>
                </div>
            )}

            {genError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
                    <i className="fas fa-exclamation-circle text-xl"></i>
                    <div>
                        <div className="font-semibold">{genError}</div>
                        {!hasActiveSub && (
                            <div className="text-sm mt-1">
                                <Link to="/pricing" className="font-semibold" style={{ color: '#e94560' }}>View pricing plans →</Link>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
                <div>
                    <h1 className="text-2xl font-extrabold" style={{ color: '#1a1a2e' }}>
                        Good day, {data?.user?.name?.split(' ')[0] || 'there'} 👋
                    </h1>
                    <p className="text-gray-500 text-sm mt-0.5">Here's your Seller Source overview</p>
                </div>
                <button onClick={() => setShowGenForm(!showGenForm)}
                    className={`flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90 ${!hasActiveSub ? 'opacity-60' : ''}`}
                    style={{ background: '#e94560' }}
                    disabled={!hasActiveSub}>
                    <i className="fas fa-plus"></i> Generate Report
                </button>
            </div>

            {/* Generate form */}
            {showGenForm && (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6 mb-8">
                    <h3 className="font-bold text-gray-900 mb-4">
                        <i className="fas fa-robot mr-2" style={{ color: '#e94560' }}></i>Generate New Report
                    </h3>
                    <div className="grid sm:grid-cols-3 gap-4 mb-5">
                        {/* Category */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Category <span style={{ color: '#e94560' }}>*</span>
                            </label>
                            <div className="relative">
                                <select value={category}
                                    onChange={e => { setCategory(e.target.value); setProductType(PRODUCT_TYPES[e.target.value]?.[0] || ''); }}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none text-sm appearance-none pr-8"
                                    style={{ borderColor: category ? '#e94560' : undefined }}>
                                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <i className="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
                            </div>
                        </div>
                        {/* Product Type */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Product Type <span style={{ color: '#e94560' }}>*</span>
                            </label>
                            <div className="relative">
                                <select value={productType} onChange={e => setProductType(e.target.value)}
                                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none text-sm appearance-none pr-8"
                                    style={{ borderColor: productType ? '#e94560' : undefined }}>
                                    {(PRODUCT_TYPES[category] || ['General']).map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                                <i className="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">Specific type within {category}</p>
                        </div>
                        {/* Min ROI */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                Min ROI <span style={{ color: '#e94560' }}>*</span>
                            </label>
                            <div className="relative">
                                <select value={minRoi} onChange={e => setMinRoi(e.target.value)}
                                    className="w-full px-4 py-3 border-2 rounded-xl focus:outline-none text-sm appearance-none pr-8"
                                    style={{ borderColor: '#e94560' }}>
                                    {Array.from({ length: 10 }, (_, i) => (i + 1) * 10).map(r => (
                                        <option key={r} value={String(r)}>{r}%</option>
                                    ))}
                                </select>
                                <i className="fas fa-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"></i>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">Minimum return on investment</p>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <button onClick={generateReport} disabled={generating}
                            className="px-6 py-3 rounded-xl font-bold text-white text-sm transition-all hover:opacity-90 disabled:opacity-60 flex items-center gap-2"
                            style={{ background: '#e94560' }}>
                            {generating ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                    Generating...
                                </>
                            ) : <><i className="fas fa-bolt"></i> Generate Now</>}
                        </button>
                        <button onClick={() => setShowGenForm(false)}
                            className="px-4 py-3 rounded-xl font-semibold text-sm text-gray-500 border border-gray-200 hover:bg-gray-50">
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard icon="fa-crown" label="Current Plan" value={stats.planLabel || 'No Plan'} color={planColor} />

                {/* Reports Today — increases as reports are generated */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: 'rgba(233,69,96,0.1)' }}>
                        <i className="fas fa-file-alt text-sm" style={{ color: '#e94560' }}></i>
                    </div>
                    <div className="text-xs text-gray-500 font-medium mb-1">Reports Today</div>
                    <div className="text-3xl font-extrabold mb-0.5" style={{ color: '#e94560' }}>
                        {stats.reportsToday || 0}
                    </div>
                    <div className="text-xs text-gray-400 mb-2">of {stats.dailyLimit || 4} generated</div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(100, ((stats.reportsToday || 0) / (stats.dailyLimit || 4)) * 100)}%`, background: '#e94560' }} />
                    </div>
                </div>

                {/* Reports Remaining — decreases down to zero */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: 'rgba(39,174,96,0.1)' }}>
                        <i className="fas fa-download text-sm" style={{ color: '#27ae60' }}></i>
                    </div>
                    <div className="text-xs text-gray-500 font-medium mb-1">Reports Remaining</div>
                    <div className="text-3xl font-extrabold mb-0.5" style={{ color: '#27ae60' }}>
                        {hasActiveSub ? (stats.reportsRemaining ?? stats.dailyLimit ?? 4) : '—'}
                    </div>
                    <div className="text-xs text-gray-400 mb-2">
                        {hasActiveSub ? `left today · resets in ${stats.reportsResetIn || 24}h` : 'subscribe to unlock'}
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full transition-all duration-500"
                            style={{ width: hasActiveSub ? `${Math.min(100, ((stats.reportsRemaining ?? stats.dailyLimit ?? 4) / (stats.dailyLimit || 4)) * 100)}%` : '0%', background: '#27ae60' }} />
                    </div>
                </div>

                <StatCard icon="fa-folder-open" label="Total Reports" value={stats.totalReports || 0} color="#0f3460" />
            </div>

            {/* Apify Data Integration Banner */}
            {apify && (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-6">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                style={{ background: apify.connected ? 'rgba(39,174,96,0.12)' : 'rgba(233,69,96,0.12)' }}>
                                <i className={`fas ${apify.connected ? 'fa-plug' : 'fa-exclamation-triangle'} text-xs`}
                                    style={{ color: apify.connected ? '#27ae60' : '#e94560' }}></i>
                            </div>
                            <div className="min-w-0">
                                <div className="text-xs font-semibold text-gray-800">
                                    Apify Data Integration
                                    <span className={`ml-2 px-1.5 py-0.5 rounded text-xs font-bold ${apify.connected ? 'text-green-700 bg-green-100' : 'text-red-600 bg-red-100'}`}>
                                        {apify.connected ? 'CONNECTED' : 'OFFLINE'}
                                    </span>
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                    {apify.connected
                                        ? `${apify.username || 'Account'} · ${(apify.plan || 'free').toUpperCase()} plan · Reports use live scraped data when available`
                                        : (apify.error || 'Set APIFY_API_TOKEN to enable live data')}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-4 text-center">
                            <div>
                                <div className="text-lg font-extrabold" style={{ color: '#1a1a2e' }}>{apify.total || 0}</div>
                                <div className="text-xs text-gray-400">Stores</div>
                            </div>
                            <div className="h-8 w-px bg-gray-100"></div>
                            <div className="flex flex-wrap gap-1 max-w-xs">
                                {['Walmart','Target','Best Buy','Costco','eBay','Newegg','Home Depot','REI'].map(s => (
                                    <span key={s} className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">{s}</span>
                                ))}
                                <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">+{Math.max(0,(apify.total||42)-8)} more</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Outreach Control Panel */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
                    <div>
                        <h3 className="font-bold text-gray-900 text-base">
                            <i className="fas fa-paper-plane mr-2" style={{ color: '#e94560' }}></i>
                            Seller Outreach
                        </h3>
                        <p className="text-xs text-gray-400 mt-0.5">
                            3-email sequence → relay.app call for non-responders
                        </p>
                    </div>
                    <div className="flex gap-2 flex-wrap justify-end">
                        <button
                            onClick={launchOutreach}
                            disabled={launching || outreach?.running}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90 disabled:opacity-60"
                            style={{ background: '#e94560' }}>
                            {(launching || outreach?.running) ? (
                                <>
                                    <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                    Running…
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-play text-xs"></i>
                                    Start Email 1
                                </>
                            )}
                        </button>
                        <button
                            onClick={triggerCalls}
                            disabled={triggeringCalls || outreach?.calling || !outreach?.pendingCalls}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90 disabled:opacity-60"
                            style={{ background: outreach?.pendingCalls > 0 ? '#0f3460' : '#9ca3af' }}
                            title={!outreach?.pendingCalls ? 'No sellers have completed all 3 emails yet' : `Call ${outreach.pendingCalls} seller(s) via relay.app`}>
                            {(triggeringCalls || outreach?.calling) ? (
                                <>
                                    <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                    Calling…
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-phone text-xs"></i>
                                    Trigger Calls {outreach?.pendingCalls > 0 && <span className="ml-1 bg-white/20 rounded-full px-1.5 py-0.5 text-xs">{outreach.pendingCalls}</span>}
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {launchMsg && (
                    <div className="mb-3 px-4 py-2.5 rounded-lg text-sm font-medium"
                        style={{ background: 'rgba(233,69,96,0.08)', color: '#c73652', border: '1px solid rgba(233,69,96,0.2)' }}>
                        <i className="fas fa-info-circle mr-2"></i>{launchMsg}
                    </div>
                )}
                {callMsg && (
                    <div className="mb-3 px-4 py-2.5 rounded-lg text-sm font-medium"
                        style={{ background: 'rgba(15,52,96,0.07)', color: '#0f3460', border: '1px solid rgba(15,52,96,0.18)' }}>
                        <i className="fas fa-phone mr-2"></i>{callMsg}
                    </div>
                )}

                {outreach && (
                    <>
                        {/* Email funnel stats */}
                        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
                            <div className="rounded-xl p-3 text-center" style={{ background: '#f8f9fc' }}>
                                <div className="text-xl font-extrabold" style={{ color: '#1a1a2e' }}>{outreach.totalSent || 0}</div>
                                <div className="text-xs text-gray-500 mt-0.5">Email 1 Sent</div>
                            </div>
                            <div className="rounded-xl p-3 text-center" style={{ background: outreach.pendingEmail2 > 0 ? 'rgba(233,69,96,0.06)' : '#f8f9fc' }}>
                                <div className="text-xl font-extrabold" style={{ color: outreach.pendingEmail2 > 0 ? '#e94560' : '#1a1a2e' }}>{outreach.pendingEmail2 || 0}</div>
                                <div className="text-xs text-gray-500 mt-0.5">Due Email 2</div>
                            </div>
                            <div className="rounded-xl p-3 text-center" style={{ background: outreach.pendingEmail3 > 0 ? 'rgba(233,69,96,0.06)' : '#f8f9fc' }}>
                                <div className="text-xl font-extrabold" style={{ color: outreach.pendingEmail3 > 0 ? '#e94560' : '#1a1a2e' }}>{outreach.pendingEmail3 || 0}</div>
                                <div className="text-xs text-gray-500 mt-0.5">Due Email 3</div>
                            </div>
                            <div className="rounded-xl p-3 text-center col-span-1" style={{ background: outreach.pendingCalls > 0 ? 'rgba(15,52,96,0.07)' : '#f8f9fc' }}>
                                <div className="text-xl font-extrabold" style={{ color: outreach.pendingCalls > 0 ? '#0f3460' : '#1a1a2e' }}>{outreach.pendingCalls || 0}</div>
                                <div className="text-xs text-gray-500 mt-0.5">Due Calls</div>
                            </div>
                            <div className="rounded-xl p-3 text-center" style={{ background: '#f8f9fc' }}>
                                <div className="text-xl font-extrabold" style={{ color: '#1a1a2e' }}>{outreach.calledTotal || 0}</div>
                                <div className="text-xs text-gray-500 mt-0.5">Called</div>
                            </div>
                            <div className="rounded-xl p-3 text-center" style={{ background: '#f8f9fc' }}>
                                <div className="text-xl font-extrabold" style={{ color: '#1a1a2e' }}>{outreach.pipelineSize || 0}</div>
                                <div className="text-xs text-gray-500 mt-0.5">Pipeline</div>
                            </div>
                        </div>

                        {/* Sequence legend */}
                        <div className="flex flex-wrap gap-2 text-xs text-gray-400">
                            <span><i className="fas fa-envelope mr-1"></i>Email 1 → Day 0</span>
                            <span className="text-gray-300">·</span>
                            <span><i className="fas fa-envelope mr-1"></i>Email 2 → Day 3</span>
                            <span className="text-gray-300">·</span>
                            <span><i className="fas fa-envelope mr-1"></i>Email 3 → Day 6</span>
                            <span className="text-gray-300">·</span>
                            <span style={{ color: '#0f3460', fontWeight: 600 }}><i className="fas fa-phone mr-1"></i>Call → Day 9 (relay.app)</span>
                        </div>
                    </>
                )}

                {outreach?.lastRunDate && (
                    <p className="text-xs text-gray-400 mt-3">
                        <i className="fas fa-clock mr-1"></i>
                        Last run: {new Date(outreach.lastRunDate).toLocaleString()}
                    </p>
                )}
            </div>

            {/* Plan + Recent Reports */}
            <div className="grid lg:grid-cols-3 gap-6">
                {/* Subscription card */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6">
                        <h3 className="font-bold text-gray-900 mb-4"><i className="fas fa-credit-card mr-2" style={{ color: '#e94560' }}></i>Subscription</h3>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold mb-3"
                            style={{ background: `${planColor}20`, color: planColor }}>
                            <div className="w-2 h-2 rounded-full" style={{ background: planColor }}></div>
                            {stats.planLabel}
                        </div>
                        {data?.user?.subscription_end_date && (
                            <p className="text-sm text-gray-500 mb-4">Renews: {new Date(data.user.subscription_end_date).toLocaleDateString()}</p>
                        )}
                        {!hasActiveSub ? (
                            <div className="space-y-2">
                                <p className="text-xs text-gray-500 mb-3">Get unlimited daily reports with a subscription plan.</p>
                                <button onClick={() => checkout('monthly')} className="w-full py-2.5 rounded-xl font-bold text-white text-sm" style={{ background: '#e94560' }}>
                                    Monthly — $29.99/mo
                                </button>
                                <button onClick={() => checkout('yearly')} className="w-full py-2.5 rounded-xl font-bold text-white text-sm" style={{ background: '#1a1a2e' }}>
                                    Yearly — $299.99/yr
                                </button>
                                <button onClick={() => checkout('onetime')} className="w-full py-2.5 rounded-xl font-semibold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50">
                                    One-Time Report — $5.99
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <Link to="/billing" className="block w-full py-2.5 text-center rounded-xl font-semibold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50">
                                    Manage Subscription
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Recent Billing */}
                    {data?.recentBilling?.length > 0 && (
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                            <h3 className="font-bold text-gray-900 mb-4"><i className="fas fa-receipt mr-2" style={{ color: '#e94560' }}></i>Recent Billing</h3>
                            <div className="space-y-3">
                                {data.recentBilling.map(b => (
                                    <div key={b.id} className="flex items-center justify-between text-sm">
                                        <div>
                                            <div className="font-medium text-gray-800 capitalize">{b.plan} plan</div>
                                            <div className="text-gray-400 text-xs">{new Date(b.createdAt).toLocaleDateString()}</div>
                                        </div>
                                        <div className="font-bold text-gray-900">${b.amount.toFixed(2)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Recent Reports */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold text-gray-900"><i className="fas fa-file-pdf mr-2" style={{ color: '#e94560' }}></i>Recent Reports</h3>
                            <Link to="/reports" className="text-sm font-semibold" style={{ color: '#e94560' }}>View all →</Link>
                        </div>
                        {data?.recentReports?.length > 0 ? (
                            <div className="space-y-3">
                                {data.recentReports.map(r => (
                                    <ReportRow key={r.id} report={r} authFetch={authFetch} />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(233,69,96,0.1)' }}>
                                    <i className="fas fa-file-alt text-2xl" style={{ color: '#e94560' }}></i>
                                </div>
                                <p className="text-gray-500 font-medium">No reports yet</p>
                                <p className="text-gray-400 text-sm mt-1">Generate your first report to get started</p>
                                {!hasActiveSub && (
                                    <Link to="/pricing" className="inline-block mt-4 px-5 py-2.5 rounded-xl font-bold text-white text-sm" style={{ background: '#e94560' }}>
                                        Get a Plan
                                    </Link>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatCard({ icon, label, value, color }) {
    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${color}15` }}>
                <i className={`fas ${icon} text-sm`} style={{ color }}></i>
            </div>
            <div className="text-xs text-gray-500 font-medium mb-1">{label}</div>
            <div className="text-xl font-extrabold" style={{ color: '#1a1a2e' }}>{value}</div>
        </div>
    );
}

function ReportRow({ report, authFetch }) {
    const [downloading, setDownloading] = useState(false);

    const download = async () => {
        setDownloading(true);
        try {
            const res = await authFetch(`/api/reports/${report.id}/download`);
            if (!res.ok) throw new Error('Download failed');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = report.filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            alert('Download failed: ' + err.message);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-red-100 hover:bg-red-50/30 transition-colors">
            <div className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(233,69,96,0.1)' }}>
                <i className="fas fa-file-pdf text-sm" style={{ color: '#e94560' }}></i>
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-800 truncate">{report.category} — {report.product_type}</div>
                <div className="text-xs text-gray-400">{new Date(report.createdAt).toLocaleDateString()} · {report.download_count} downloads</div>
            </div>
            <button onClick={download} disabled={downloading}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all hover:opacity-90 disabled:opacity-60"
                style={{ background: '#1a1a2e' }}>
                {downloading ? <span className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin"></span> : <i className="fas fa-download"></i>}
                {downloading ? 'Downloading' : 'Download'}
            </button>
        </div>
    );
}
